import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRouter } from 'next/router';

import { Dialog, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { nanoid } from 'nanoid';
import {
  type ClusterType,
  type PagePreviewMessageEvent,
  type PagePreviewResult,
  type PageRankingMessageEvent,
  type PageRankingSearchResult,
  type SearchResult,
  type WorkerJobResult,
} from 'searchPreview.worker';

import { LoadingIcon, SearchIcon } from '@/components/icons/ElementIcons';
import { ComponentizeString } from '@/components/shared/ComponentizeString';
import { usePagination } from '@/components/shared/docs/Pagination';

import { maxUserSearchLength } from '@/lib/docs/findBestMatch';

import { inter } from '@/styles/fonts';

import _allMdxFileContentTree from '@/data/docs/_allMdxFileContentTree.json';
import { removedWords } from '@/data/docs/removedSearchWords.mjs';
import { debounceV2 } from '@/shared-functions/debounceV2';

const PreviewLoader = () => {
  return (
    <div className='animate-pulse flex space-x-4 px-4 pb-4'>
      <div className='h-5 w-5 rounded-full bg-slate-700'></div>
      <div className='flex-1 space-y-6 py-1'>
        <div className='space-y-3'>
          <div className='grid grid-cols-3 gap-4'>
            <div className='col-span-2 h-2 rounded bg-slate-700'></div>
            <div className='col-span-1 h-2 rounded bg-slate-700'></div>
          </div>
          <div className='h-2 rounded bg-slate-700'></div>
        </div>
      </div>
    </div>
  );
};

function SearchDialog({
  open,
  setOpen,
  className,
}: {
  open: boolean;
  setOpen: (state: boolean) => void;
  className: string;
}) {
  let router = useRouter();

  useEffect(() => {
    if (!open) {
      return;
    }

    function onRouteChange() {
      setOpen(false);
    }

    router.events.on('routeChangeStart', onRouteChange);
    router.events.on('hashChangeStart', onRouteChange);

    return () => {
      router.events.off('routeChangeStart', onRouteChange);
      router.events.off('hashChangeStart', onRouteChange);
    };
  }, [open, setOpen, router]);

  useEffect(() => {
    if (open) {
      return;
    }

    function onKeyDown(event: any) {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen(true);
      }
    }

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, setOpen]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog onClose={setOpen} className={clsx('fixed inset-0 z-[10000]', className)}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-zinc-400/25 backdrop-blur-sm dark:bg-black/40' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto px-4 py-4 sm:px-6 sm:py-20 md:py-8 lg:px-8 lg:py-[5vh]'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 scale-95'
            enterTo='opacity-100 scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'
          >
            <Dialog.Panel className='mx-auto overflow-hidden rounded-lg bg-zinc-50 shadow-xl ring-1 ring-zinc-900/7.5 dark:bg-zinc-900 dark:ring-zinc-800 sm:max-w-xl'>
              <SearchInput />
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function useSearchProps() {
  let buttonRef = useRef<HTMLButtonElement | null>(null);
  let [open, setOpen] = useState(false);

  return {
    buttonProps: {
      ref: buttonRef,
      onClick() {
        setOpen(true);
      },
    },
    dialogProps: {
      open,
      setOpen(open: boolean) {
        if (buttonRef.current) {
          let { width, height } = buttonRef.current.getBoundingClientRect();
          if (!open || (width !== 0 && height !== 0)) {
            setOpen(open);
          }
        }
      },
    },
  };
}

const createSearchCluster = (searchResult: SearchResult[]) => {
  let searchCluster = new Map<string, ClusterType[]>();

  Object.values(searchResult).forEach((searchItemData) => {
    let indexSignature = searchItemData.path.split('/')[0];
    if (searchCluster.get(indexSignature)) {
      searchCluster.set(indexSignature, [
        ...(searchCluster.get(indexSignature) as ClusterType[]),
        { clusterName: indexSignature, ...searchItemData },
      ]);
    } else {
      searchCluster.set(indexSignature, [{ clusterName: indexSignature, ...searchItemData }]);
    }
  });
  return searchCluster;
};

const SearchPreview = ({
  searchCluster,
  searchTerms,
}: {
  searchCluster: ClusterType;
  searchTerms: string;
}) => {
  const [searchPreview, setSearchPreview] = useState<WorkerJobResult[]>([]);
  const [imLoading, setImLoading] = useState({ id: '' });

  const heavyComputationWorker = useRef<Worker>();

  useEffect(() => {
    heavyComputationWorker.current = new Worker(
      new URL('../../../../searchPreview.worker', import.meta.url),
    );
  }, []);

  useEffect(() => {
    let path = searchCluster.path;
    let key = path as keyof typeof _allMdxFileContentTree;
    if (!heavyComputationWorker.current) return;

    const message: PagePreviewMessageEvent = {
      type: 'get-search-preview',
      data: {
        searchTerms: searchTerms,
        searchContent: _allMdxFileContentTree[key],
        path,
      },
    };
    heavyComputationWorker.current.postMessage(message);
    setImLoading({ id: 'generatingPreview' });

    heavyComputationWorker.current.onmessage = function (result: MessageEvent<PagePreviewResult>) {
      const { type, data, status } = result.data;
      switch (type) {
        case 'page-search-preview-result':
          setImLoading({ id: '' });

          if (result.data) {
            setSearchPreview(data);
          }
          break;

        default:
          break;
      }
    };
    // Clean up the Web Worker when the component unmounts
    return () => {
      if (heavyComputationWorker.current) heavyComputationWorker.current.terminate();
    };
  }, [searchCluster.path, searchCluster.docsPageTitle, searchTerms]);

  const { goToNext, goToPrev, currentItems, hasNext, hasPrev, totalPages, currentPage } =
    usePagination<WorkerJobResult>(searchPreview);

  return (
    <div className='mt-2 w-full rounded-sm bg-slate-800 hover:ring-2 hover:ring-sky-800'>
      <div className='flex items-center justify-between gap-3 p-4'>
        <a
          href={`/docs/${searchCluster.path}`}
          className='block text-sm font-medium text-slate-500 hover:underline md:text-base'
        >
          {searchCluster.docsPageTitle}
        </a>
        <p className='text-xs font-medium text-sky-500'>
          ({currentPage}/{totalPages})
        </p>
      </div>

      {imLoading.id === 'generatingPreview'
        ? Array(Math.ceil(Math.random() * 4))
            .fill(0)
            .map((_, index) => <PreviewLoader key={index} />)
        : currentItems.map(({ content, path }, index) => {
            return (
              <a
                key={`${path}-${content.closestElementReference}-${index}`}
                href={`/docs/${path}#${content.closestElementReference}`}
                className='grid grid-flow-col justify-start pb-4 pl-4 text-sm font-medium text-slate-500 hover:underline md:text-base'
              >
                <svg
                  className='mr-2 h-[24px] w-[24px] text-sky-500'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                >
                  <path
                    d='M13 13h4-4V8H7v5h6v4-4H7V8H3h4V3v5h6V3v5h4-4v5zm-6 0v4-4H3h4z'
                    stroke='currentColor'
                    fill='none'
                    fillRule='evenodd'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  ></path>
                </svg>
                <ComponentizeString
                  classNaming='whitespace-break-spaces break-all'
                  htmlString={content.text}
                />
              </a>
            );
          })}
      {(hasNext || hasPrev) && (
        <div className='not-prose flex justify-end gap-2 px-4 py-2 text-xs text-slate-400'>
          <button
            className='rounded-md border border-slate-600 px-2 disabled:cursor-not-allowed disabled:opacity-40'
            disabled={!hasPrev}
            onClick={goToPrev}
          >
            Prev
          </button>
          <button
            className='rounded-md border border-slate-600 px-2 disabled:cursor-not-allowed disabled:opacity-40'
            disabled={!hasNext}
            onClick={goToNext}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const SearchResult = ({
  clusterName,
  clusterResults,
  searchTerms,
}: {
  clusterName: string;
  clusterResults: ClusterType[];
  searchTerms: string;
}) => {
  return (
    <>
      <p className='mt-4 block text-sm font-bold text-sky-500'>{clusterName}</p>
      {clusterResults.map((searchCluster) => {
        return (
          <SearchPreview searchCluster={searchCluster} searchTerms={searchTerms} key={nanoid(12)} />
        );
      })}
    </>
  );
};

const SearchInput = () => {
  const [SearchResultsReadonly, setSearchResultsReadonly] = useState<SearchResult[]>([]);
  const [imLoading, setImLoading] = useState({ id: '' });
  // modify this one for filtering and etc...
  const [searchResults, setSearchResults] = useState<Map<string, ClusterType[]>>(new Map());
  const [searchTerm, setSearchTerm] = useState<string>('');

  const heavyComputationWorker = useRef<Worker>();

  useEffect(() => {
    heavyComputationWorker.current = new Worker(
      new URL('../../../../searchPreview.worker', import.meta.url),
    );

    heavyComputationWorker.current.onmessage = function (
      result: MessageEvent<PageRankingSearchResult>,
    ) {
      setImLoading({ id: '' });
      const { type, data, status } = result.data;
      switch (type) {
        case 'page-ranking':
          setSearchResultsReadonly(data);

          break;

        default:
          break;
      }
    };

    // Clean up the Web Worker when the component unmounts
    return () => {
      if (heavyComputationWorker.current) heavyComputationWorker.current.terminate();
    };
  }, []);

  const searchDocs = useMemo(
    () =>
      debounceV2(() => {
        if (!heavyComputationWorker.current) return;

        const message: PageRankingMessageEvent = {
          type: 'get-page-ranking',
          data: { searchTerms: searchTerm },
        };
        heavyComputationWorker.current.postMessage(message);
        // setImLoading({ id: 'generatingPreview' });
      }),
    [searchTerm],
  );
  useEffect(() => {
    setSearchResults(createSearchCluster(SearchResultsReadonly));
  }, [SearchResultsReadonly]);

  useEffect(() => {
    searchDocs();
  }, [searchDocs]);

  const acceptedUserSearchTerms = searchTerm
    .trim()
    .split(' ')
    .filter((eachWord: string) => {
      if (!removedWords.some((removedWord) => removedWord === eachWord.toLowerCase()))
        return eachWord;
    });

  return (
    <div className='h-auto max-h-[90vh] overflow-auto bg-brand-dark p-4 lg:max-h-[80vh]'>
      <div
        className={clsx(
          'group sticky left-0 top-0 flex h-12 lg:relative lg:left-auto lg:top-auto lg:bg-transparent lg:bg-none',
          searchTerm && 'bg-brand-light',
        )}
      >
        <SearchIcon className='pointer-events-none absolute left-3 top-0 h-full w-5 stroke-slate-400' />
        <input
          onChange={(event) => {
            // Start loader while user is still typing
            setImLoading({ id: 'searchingDocs' });
            setSearchTerm(event.target.value);
          }}
          value={searchTerm}
          placeholder='Search docs...'
          className={clsx(
            'flex-auto appearance-none pl-10 text-zinc-900 outline-none placeholder:text-zinc-500 focus:w-full focus:flex-none dark:bg-slate-800/75 dark:text-white sm:text-sm',
          )}
        />
        <div className='absolute inset-y-0 right-3 flex items-center'>
          {imLoading.id === 'searchingDocs' && (
            <LoadingIcon className='animate-spin h-5 w-5 stroke-zinc-200 text-transparent dark:stroke-transparent dark:text-sky-500' />
          )}
        </div>
      </div>
      {acceptedUserSearchTerms.length > maxUserSearchLength &&
        !!Array.from(searchResults).length && (
          <div className='mt-2 flex items-center justify-end gap-1 overflow-hidden truncate break-all text-end text-xs text-slate-400'>
            <p>Showing results for</p>{' '}
            {acceptedUserSearchTerms.map((term: string, index) => {
              return (
                <span className={`text-sky-500`} key={nanoid(12)}>
                  {index > maxUserSearchLength - 1 ? <s>{term.trim()}</s> : term.trim()}
                </span>
              );
            })}
          </div>
        )}
      <div className=''>
        {!!Array.from(searchResults).length
          ? Array.from(searchResults).map(([key, value]) => {
              return (
                <SearchResult
                  key={nanoid(12)}
                  clusterName={key}
                  searchTerms={searchTerm}
                  // top 5 results of each
                  clusterResults={value.slice(0, 5)}
                />
              );
            })
          : searchTerm &&
            imLoading.id !== 'searchingDocs' && (
              <div className='flex items-center justify-center pt-4 text-lg text-white'>
                <h2>
                  No results found for: <span className='font-bold'>{`"${searchTerm}"`}</span>
                </h2>
              </div>
            )}
      </div>
    </div>
  );
};

export const SearchBar = () => {
  let [modifierKey, setModifierKey] = useState('Ctrl');
  let { buttonProps, dialogProps } = useSearchProps();

  useEffect(() => {
    setModifierKey(/(Mac|iPhone|iPod|iPad)/i.test(navigator.platform) ? '⌘' : 'Ctrl ');
  }, []);

  return (
    <div className={`lg:block lg:max-w-md lg:flex-auto`}>
      <button
        type='button'
        className='group flex h-6 w-6 items-center justify-between rounded-md dark:bg-slate-800/75 dark:hover:bg-slate-700/40 md:h-auto md:w-auto md:rounded-lg md:px-2.5 md:py-2.5 md:text-sm md:ring-1 md:ring-slate-200 md:hover:ring-slate-300 dark:md:ring-inset dark:md:ring-white/5 dark:md:hover:ring-slate-500 lg:h-auto lg:w-60 lg:flex-none lg:py-1.5 lg:pr-3.5 xl:w-72 xl:px-4'
        {...buttonProps}
      >
        <div className='flex flex-row'>
          <SearchIcon className='h-5 w-5 flex-none fill-slate-100 group-hover:fill-slate-200 dark:fill-slate-200 md:group-hover:fill-slate-100' />
          <span className='hidden flex-row items-center md:not-sr-only md:ml-2 md:text-slate-500 md:dark:text-slate-400 lg:flex'>
            Search docs
          </span>
        </div>
        <div className='ml-2 hidden flex-row items-center md:text-slate-500 md:dark:text-slate-400 lg:flex'>
          <span className='keyboard-button'>{modifierKey}</span>
          <span>+</span>
          <span className='keyboard-button'>K</span>
        </div>
      </button>
      <SearchDialog className={`${inter.variable} font-sans`} {...dialogProps} />
    </div>
  );
};
